/**
 * 统计与仪表盘接口。路径见方案 4.5。
 *
 * | 方法 | 路径 |
 * | --- | --- |
 * | GET | `/stats/overview?studentId&range` |
 * | GET | `/stats/timeseries?studentId&metric=completion\|duration\|points&interval=day\|week\|month` |
 * | GET | `/stats/nodes?studentId&nodeId` |
 * | GET | `/stats/heatmap?studentId&year` |
 * | GET | `/stats/export?studentId&format=csv\|xlsx` |
 *
 * 容错：每个函数都返回带 `notReady` 标记的结果；未就绪时页面显示
 * "该功能尚未就绪"，而不是白屏（见任务说明的降级要求）。
 */
import { api, download, tryRequest } from './client';
import { buildTree } from './nodes';
import { pickArray, pickObject, toNumber, toOptionalNumber } from './shape';
import type {
  HeatmapDay,
  HeatmapResponse,
  NodeSummary,
  StatsOverview,
  TimeSeriesPoint,
  TimeSeriesResponse,
} from './types';

// ---------------------------------------------------------------- 概览

export interface StatsOverviewResult {
  overview: StatsOverview;
  notReady: boolean;
}

const EMPTY_OVERVIEW: StatsOverview = {
  completionRate: null,
  streak: 0,
  totalMinutes: 0,
};

export async function getOverview(
  studentId: string,
  range: { from?: string; to?: string } = {},
): Promise<StatsOverviewResult> {
  const result = await tryRequest(() =>
    api.get<unknown>('/stats/overview', { studentId, ...range }),
  );
  if (result.state === 'notReady') return { overview: EMPTY_OVERVIEW, notReady: true };

  const record = pickObject(result.data, {}) as Record<string, unknown>;
  // 完成率后端可能给 0~1 或 0~100，统一收敛到 0~1（>1 视为百分数）
  const rawRate = toOptionalNumber(record.completionRate ?? record.completion_rate);
  const completionRate =
    rawRate === null ? null : rawRate > 1 ? Math.min(1, rawRate / 100) : Math.max(0, rawRate);

  return {
    overview: {
      completionRate,
      streak: toNumber(record.streak, 0),
      totalMinutes: toNumber(record.totalMinutes ?? record.total_minutes, 0),
      scheduledCount: toOptionalNumber(record.scheduledCount) ?? undefined,
      doneCount: toOptionalNumber(record.doneCount) ?? undefined,
      missedCount: toOptionalNumber(record.missedCount) ?? undefined,
      makeupCount: toOptionalNumber(record.makeupCount) ?? undefined,
      rangeFrom: typeof record.rangeFrom === 'string' ? record.rangeFrom : undefined,
      rangeTo: typeof record.rangeTo === 'string' ? record.rangeTo : undefined,
      weekTrend: normalizeSeries(record.weekTrend ?? record.week_trend).points,
    },
    notReady: false,
  };
}

// ---------------------------------------------------------------- 趋势

export type TimeseriesMetric = 'completion' | 'duration' | 'points';
export type TimeseriesInterval = 'day' | 'week' | 'month';

export interface TimeseriesResult {
  series: TimeSeriesResponse;
  notReady: boolean;
}

function normalizeSeries(raw: unknown): TimeSeriesResponse {
  const list = pickArray<unknown>(raw, 'points', 'series', 'items', 'data');
  const points: TimeSeriesPoint[] = [];
  for (const item of list) {
    if (item === null || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const bucket =
      (typeof record.bucket === 'string' && record.bucket) ||
      (typeof record.date === 'string' && record.date) ||
      (typeof record.label === 'string' && record.label) ||
      '';
    if (!bucket) continue;
    const ratio = toOptionalNumber(record.ratio ?? record.completionRate);
    points.push({
      bucket,
      ratio: ratio === null ? null : ratio > 1 ? Math.min(1, ratio / 100) : ratio,
      minutes: toOptionalNumber(record.minutes ?? record.durationMinutes),
      points: toOptionalNumber(record.points ?? record.delta),
      scheduled: toOptionalNumber(record.scheduled) ?? undefined,
      done: toOptionalNumber(record.done) ?? undefined,
    });
  }

  const record = (pickObject(raw, {}) ?? {}) as Record<string, unknown>;
  return {
    metric: typeof record.metric === 'string' ? record.metric : 'completion',
    interval: typeof record.interval === 'string' ? record.interval : 'day',
    points,
  };
}

export async function getTimeseries(
  studentId: string,
  metric: TimeseriesMetric,
  interval: TimeseriesInterval,
  range: { from?: string; to?: string } = {},
): Promise<TimeseriesResult> {
  const result = await tryRequest(() =>
    api.get<unknown>('/stats/timeseries', { studentId, metric, interval, ...range }),
  );
  if (result.state === 'notReady') {
    return { series: { metric, interval, points: [] }, notReady: true };
  }
  return { series: normalizeSeries(result.data), notReady: false };
}

// ---------------------------------------------------------------- 进度树

export interface StatsNodesResult {
  nodes: NodeSummary[];
  notReady: boolean;
}

export async function getStatsNodes(
  studentId: string,
  nodeId?: string,
): Promise<StatsNodesResult> {
  const result = await tryRequest(() =>
    api.get<unknown>('/stats/nodes', { studentId, ...(nodeId ? { nodeId } : {}) }),
  );
  if (result.state === 'notReady') return { nodes: [], notReady: true };
  return {
    nodes: buildTree(pickArray<unknown>(result.data, 'nodes', 'tree', 'items')),
    notReady: false,
  };
}

// ---------------------------------------------------------------- 热力图

export interface HeatmapResult {
  heatmap: HeatmapResponse;
  notReady: boolean;
}

export async function getHeatmap(studentId: string, year: number): Promise<HeatmapResult> {
  const result = await tryRequest(() => api.get<unknown>('/stats/heatmap', { studentId, year }));
  if (result.state === 'notReady') {
    return { heatmap: { year, days: [], total: 0 }, notReady: true };
  }

  const record = pickObject(result.data, {}) as Record<string, unknown>;
  const days: HeatmapDay[] = [];
  for (const item of pickArray<unknown>(record, 'days', 'items', 'heatmap')) {
    if (item === null || typeof item !== 'object') continue;
    const day = item as Record<string, unknown>;
    const date = typeof day.date === 'string' ? day.date : '';
    if (!date) continue;
    const count = toNumber(day.count, 0);
    days.push({
      date,
      count,
      level: toOptionalNumber(day.level) ?? levelFor(count),
      minutes: toOptionalNumber(day.minutes) ?? undefined,
    });
  }

  return {
    heatmap: {
      year: toNumber(record.year, year),
      days,
      total: toOptionalNumber(record.total) ?? days.reduce((sum, d) => sum + d.count, 0),
      maxCount: toOptionalNumber(record.maxCount) ?? undefined,
    },
    notReady: false,
  };
}

/** 后端没给 level 时的分档（GitHub 风格 0~4）。 */
function levelFor(count: number): number {
  if (count <= 0) return 0;
  if (count === 1) return 1;
  if (count === 2) return 2;
  if (count <= 4) return 3;
  return 4;
}

// ---------------------------------------------------------------- 导出

/**
 * 导出明细 CSV（方案 4.5 `GET /stats/export?format=csv`）。
 *
 * 只负责把服务端返回的文件流交给浏览器下载；后端未就绪时由调用方
 * 回退到"前端自己拼一份 CSV"（见 stats 页面），保证这条入口永远可用。
 */
export async function downloadExport(
  studentId: string,
  format: 'csv' | 'xlsx' = 'csv',
  range: { from?: string; to?: string } = {},
): Promise<{ filename: string; blob: Blob }> {
  const res = await download('/stats/export', { studentId, format, ...range });
  if (!res.ok) {
    throw new Error(`导出失败（HTTP ${res.status}）`);
  }
  const blob = await res.blob();
  const today = new Date().toISOString().slice(0, 10);
  return { filename: `打卡明细-${today}.${format}`, blob };
}
